import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Calendar, Download, CheckCircle, AlertCircle, FileText, 
  Clock, ArrowRight, Sparkles, Crown, Zap, Shield, Loader2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useToast } from '../../hooks/use-toast';
import DashboardLayout from '../../components/DashboardLayout';
import { useNavigate } from 'react-router-dom';

const PLAN_ICONS = {
  free: Shield,
  freemium: Sparkles,
  basic: Zap,
  pro: Crown,
  business: Crown,
  enterprise: Crown,
};

const PLAN_COLORS = {
  free: 'from-gray-500 to-gray-600',
  freemium: 'from-violet-500 to-purple-600',
  basic: 'from-blue-500 to-indigo-600',
  pro: 'from-amber-500 to-orange-600',
  business: 'from-emerald-500 to-teal-600',
  enterprise: 'from-rose-500 to-red-600',
};

const Billing = () => {
  const { apiCall, user } = useAuth();
  const { formatPrice } = useCurrency();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const [subRes, plansRes] = await Promise.all([
        apiCall('/api/vendor-subscription/my-subscription'),
        apiCall('/api/subscription-plans/public'),
      ]);

      setSubscription(subRes?.subscription || null);
      setPlan(subRes?.plan || null);
      setAllPlans(Array.isArray(plansRes) ? plansRes.filter(p => p.status === 'published' || p.plan_code) : []);

      // Try fetching payment history
      try {
        const payments = await apiCall('/api/vendor-subscription/my-payment-history');
        setPaymentHistory(Array.isArray(payments) ? payments : []);
      } catch {
        setPaymentHistory([]);
      }
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrialDaysRemaining = () => {
    if (!subscription?.trial_end) return null;
    const trialEnd = new Date(subscription.trial_end);
    const now = new Date();
    const days = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const isOnTrial = subscription?.status === 'trial' || subscription?.status === 'trialing';
  const hasActiveSubscription = subscription && ['active', 'trial', 'trialing'].includes(subscription.status);
  const trialDays = getTrialDaysRemaining();
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  // ======================== NO PLAN STATE ========================
  if (!subscription || !plan) {
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="billing-no-plan">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Billing</h1>
            <p className="text-gray-500 mt-1 text-sm sm:text-base">Manage your subscription and billing</p>
          </div>

          {/* Trial / No Plan Banner */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <AlertCircle className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900 text-lg">No Active Plan</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    You're currently on a trial or haven't selected a plan yet. Choose a plan to unlock full features and start selling.
                  </p>
                  <Button
                    className="mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => navigate('/vendor/subscription-plans')}
                    data-testid="billing-choose-plan-btn"
                  >
                    Choose a Plan <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Available Plans Grid */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Available Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allPlans.filter(p => p.plan_code !== 'free' && p.monthly_price > 0).map((p) => {
                const Icon = PLAN_ICONS[p.plan_code] || Zap;
                const gradient = PLAN_COLORS[p.plan_code] || 'from-gray-500 to-gray-600';
                return (
                  <Card key={p.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/vendor/subscription-plans')} data-testid={`billing-plan-card-${p.plan_code}`}>
                    <CardContent className="p-5">
                      <div className={`inline-flex p-2.5 rounded-xl bg-gradient-to-br ${gradient} mb-3`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="font-bold text-base">{p.plan_name}</h3>
                      {p.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{p.description}</p>}
                      <div className="mt-3">
                        <span className="text-2xl font-bold">{formatPrice(p.monthly_price)}</span>
                        <span className="text-sm text-gray-500">/mo</span>
                      </div>
                      <div className="mt-2 text-xs text-gray-500 space-y-1">
                        <p>Up to {p.max_listings === -1 ? 'Unlimited' : p.max_listings} listings</p>
                        <p>{p.commission_percentage}% commission</p>
                        {p.trial_enabled && <p className="text-green-600">{p.trial_period_days}-day free trial</p>}
                      </div>
                      <Button variant="outline" className="w-full mt-4 text-sm" size="sm">
                        View Details <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {paymentHistory.length > 0 && <PaymentHistorySection payments={paymentHistory} formatPrice={formatPrice} />}
        </div>
      </DashboardLayout>
    );
  }

  // ======================== ACTIVE PLAN STATE ========================
  const planCode = plan?.plan_code || subscription?.plan_code || 'free';
  const PlanIcon = PLAN_ICONS[planCode] || Zap;
  const planGradient = PLAN_COLORS[planCode] || 'from-gray-500 to-gray-600';

  // When the vendor is on trial, the renewal date MUST come from
  // `trial_end` — not `current_period_end`. Some legacy subscription
  // rows still have stale `current_period_end` values from the original
  // 30-day trial creation (e.g. Feb 2026) or from an annual placeholder
  // (e.g. April 2027), which is why the Billing page was showing the
  // wrong date even though `trial_end` is correctly set to 2026-05-31
  // (the platform-wide extended trial).
  const renewalDate = isOnTrial
    ? subscription?.trial_end
    : (subscription?.current_period_end || subscription?.renewal_date || subscription?.trial_end);
  const monthlyPrice = plan?.monthly_price || 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="billing-active-plan">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Billing</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Manage your subscription and billing</p>
        </div>

        {/* Current Plan Card */}
        <Card className="overflow-hidden" data-testid="billing-current-plan">
          <div className={`bg-gradient-to-r ${planGradient} p-5 sm:p-6`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <PlanIcon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-white">{plan?.plan_name || 'Current Plan'}</h2>
                    {isOnTrial && (
                      <Badge className="bg-white/25 text-white border-white/30 text-xs">Trial</Badge>
                    )}
                    {subscription?.status === 'active' && !isOnTrial && (
                      <Badge className="bg-white/25 text-white border-white/30 text-xs">Active</Badge>
                    )}
                  </div>
                  {plan?.description && (
                    <p className="text-white/80 text-sm mt-0.5">{plan.description}</p>
                  )}
                </div>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  {monthlyPrice > 0 ? formatPrice(monthlyPrice) : 'Free'}
                </p>
                {monthlyPrice > 0 && <p className="text-white/70 text-sm">per month</p>}
              </div>
            </div>
          </div>

          <CardContent className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Billing Status */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="font-semibold text-sm capitalize">{subscription?.status || 'Active'}</p>
                </div>
              </div>

              {/* Renewal / Trial End */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="h-5 w-5 text-blue-500 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">{isOnTrial ? 'Trial Ends' : 'Next Renewal'}</p>
                  <p className="font-semibold text-sm">
                    {renewalDate
                      ? new Date(renewalDate).toLocaleDateString('en-IN', { dateStyle: 'medium' })
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Trial Days / Commission */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {isOnTrial && trialDays !== null ? (
                  <>
                    <Clock className="h-5 w-5 text-amber-500 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500">Trial Remaining</p>
                      <p className="font-semibold text-sm">{trialDays} day{trialDays !== 1 ? 's' : ''}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 text-purple-500 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500">Commission Rate</p>
                      <p className="font-semibold text-sm">{plan?.commission_percentage || 0}%</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Plan Highlights */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <PlanStat label="Listings" value={plan?.max_listings === -1 ? 'Unlimited' : plan?.max_listings || 0} />
              <PlanStat label="Listing Types" value={plan?.listing_types_included || 1} />
              <PlanStat label="Team Members" value={plan?.team_members_included || 1} />
              <PlanStat label="Locations" value={plan?.max_locations || 1} />
            </div>

            {/* Actions */}
            <div className="mt-5 flex flex-wrap gap-3">
              {isOnTrial && (
                <Button
                  onClick={() => navigate('/vendor/subscription-plans')}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                  data-testid="billing-upgrade-btn"
                >
                  Upgrade Plan <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
              {!isOnTrial && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/vendor/subscription-plans')}
                  data-testid="billing-change-plan-btn"
                >
                  Change Plan
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Trial Warning */}
        {isOnTrial && trialDays !== null && trialDays <= 7 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-amber-900">
                    {trialDays === 0 ? 'Your trial has ended' : `Your trial ends in ${trialDays} day${trialDays !== 1 ? 's' : ''}`}
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Upgrade to a paid plan to continue using all features without interruption.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment History */}
        <PaymentHistorySection payments={paymentHistory} formatPrice={formatPrice} />
      </div>
    </DashboardLayout>
  );
};

// Small stat display for plan highlights
const PlanStat = ({ label, value }) => (
  <div className="text-center p-3 bg-gray-50 rounded-lg">
    <p className="text-lg font-bold">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

// Payment history section (shared between both states)
const PaymentHistorySection = ({ payments, formatPrice }) => {
  const getStatusBadge = (status) => {
    const config = {
      paid: { color: 'bg-green-100 text-green-700', label: 'Paid' },
      success: { color: 'bg-green-100 text-green-700', label: 'Paid' },
      pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending' },
      failed: { color: 'bg-red-100 text-red-700', label: 'Failed' },
      refunded: { color: 'bg-gray-100 text-gray-700', label: 'Refunded' },
    };
    const { color, label } = config[status] || config.pending;
    return <Badge className={`${color} text-xs`}>{label}</Badge>;
  };

  if (!payments || payments.length === 0) {
    return (
      <Card data-testid="billing-payment-history">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" /> Payment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-400">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No payment history yet</p>
            <p className="text-sm mt-1">Your invoices and payment records will appear here.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="billing-payment-history">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5" /> Payment History
        </CardTitle>
        <CardDescription>View your past invoices and payments</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-semibold text-sm">Date</th>
                <th className="text-left p-3 font-semibold text-sm">Description</th>
                <th className="text-left p-3 font-semibold text-sm">Amount</th>
                <th className="text-left p-3 font-semibold text-sm">Status</th>
                <th className="text-left p-3 font-semibold text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment, idx) => (
                <tr key={payment.id || idx} className="border-b hover:bg-gray-50">
                  <td className="p-3 text-sm">{new Date(payment.date || payment.created_at).toLocaleDateString('en-IN')}</td>
                  <td className="p-3 text-sm">{payment.description || payment.plan_name || 'Subscription'}</td>
                  <td className="p-3 font-semibold">{formatPrice(payment.amount)}</td>
                  <td className="p-3">{getStatusBadge(payment.status)}</td>
                  <td className="p-3">
                    {payment.invoice_url && (
                      <Button variant="ghost" size="icon" asChild>
                        <a href={payment.invoice_url} target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {payments.map((payment, idx) => (
            <div key={payment.id || idx} className="p-4 border rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <p className="text-sm text-gray-500">
                  {new Date(payment.date || payment.created_at).toLocaleDateString('en-IN')}
                </p>
                {getStatusBadge(payment.status)}
              </div>
              <p className="text-sm font-medium">{payment.description || payment.plan_name || 'Subscription'}</p>
              <p className="text-lg font-bold mt-1">{formatPrice(payment.amount)}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default Billing;
